import type { StockKanbanSupplyAvailableLotOption } from "./loadStockKanbanSupplyLotAvailability";

/**
 * Считает максимальное количество отпуска по выбранной партии. \
 * Базовый лимит — остаток партии из `getAvLots` (`lotQtyRest`, уже в ЕИ потребности контейнера),
 * без «возврата» qty текущей строки состава. \
 * При multi-размещении (2+ ячейки) и выбранной конкретной ячейке лимит дополнительно
 * ограничивается остатком этой ячейки из `loadRemaindersByFilter` — берётся минимум,
 * чтобы не забрать из ячейки больше, чем в ней лежит, и не превысить доступный остаток партии. \
 * Пока партия не выбрана — `null` (правило `max` в form-config отключено).
 * @param lot выбранная обогащённая партия; `null` — не выбрана
 * @param spCode выбранная ячейка; `null` — ещё не выбрана (multi); `0` — «на полу»
 */
export function resolveStockKanbanSupplyContentAvailableQty(
    lot: StockKanbanSupplyAvailableLotOption | null,
    spCode: number | null,
): number | null {
    if (lot == null) {
        return null;
    }

    const { cells, lotQtyRest } = lot.availability;
    const lotRest = Math.max(lotQtyRest, 0);

    const isMultiCellSelection = cells != null && cells.length > 1 && spCode != null && spCode !== 0;
    if (!isMultiCellSelection) {
        return lotRest;
    }

    const cell = cells.find((item) => item.SpCode === spCode);
    if (cell == null) {
        return lotRest;
    }

    return Math.min(Math.max(cell.Qty, 0), lotRest);
}

/**
 * Верхний лимит qty в форме: минимум из остатка партии/ячейки и свободной ёмкости контейнера. \
 * Ёмкость = `container.Qty − Σ qty других строк состава` (текущая строка при edit не вычитается). \
 * Пока партия не выбрана — `null` (правило `max` отключено).
 * @param lot выбранная обогащённая партия; `null` — не выбрана
 * @param spCode выбранная ячейка
 * @param containerCapacityMax свободная ёмкость контейнера (≥ 0)
 */
export function resolveStockKanbanSupplyContentMaxQty(
    lot: StockKanbanSupplyAvailableLotOption | null,
    spCode: number | null,
    containerCapacityMax: number,
): number | null {
    const lotMax = resolveStockKanbanSupplyContentAvailableQty(lot, spCode);
    if (lotMax == null) {
        return null;
    }

    return Math.min(lotMax, Math.max(containerCapacityMax, 0));
}
